import { getPackageDefinitions } from "../services/PackageService";
import type { PackageDefinition } from "../constants/packages";
import { captureAppError } from "../observability/sentry";

const STORAGE_KEY = "hh_pkg_defs_v2";
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days — definitions rarely change
// FE-5: stale-as-fallback cap so severely outdated definitions are not shown indefinitely
const MAX_STALE_LIFETIME_MS = 24 * 60 * 60 * 1000; // 24 hours

type CachedDefs = { defs: PackageDefinition[]; timestamp: number };

let memory: CachedDefs | null = null;

function isCachedDefs(value: unknown): value is CachedDefs {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return typeof v.timestamp === "number" && Array.isArray(v.defs);
}

function readStorage(): CachedDefs | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    // FE-10: runtime guard — discard malformed cache entries
    return isCachedDefs(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeStorage(entry: CachedDefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch (err) {
    // FE-11: report quota errors; silently drop other write failures
    if (err instanceof DOMException && err.name === "QuotaExceededError") {
      captureAppError(err, { area: "packageDefsCache", action: "writeStorage" });
    }
  }
}

/**
 * Returns package definitions, using a two-tier cache (in-memory → localStorage)
 * with localStorage as a fallback when the backend is unreachable.
 *
 * Priority:
 *  1. In-memory, fresh   → return immediately
 *  2. localStorage, fresh → return immediately (hydrates in-memory)
 *  3. Either stale        → fetch from backend
 *       success: update both caches, return fresh data
 *       failure: return stale localStorage data so the section stays visible
 *  4. No cache at all + fetch failed → return []
 */
export async function getCachedPackageDefinitions(): Promise<PackageDefinition[]> {
  const now = Date.now();

  if (memory && now - memory.timestamp < TTL_MS) return memory.defs;

  const stored = readStorage();
  if (stored && now - stored.timestamp < TTL_MS) {
    memory = stored;
    return stored.defs;
  }

  const { data } = await getPackageDefinitions();
  if (data) {
    const entry: CachedDefs = {
      defs: data,
      timestamp: now,
    };
    memory = entry;
    writeStorage(entry);
    return entry.defs;
  }

  // Backend down — serve stale data, but only within the max stale lifetime
  if (stored && now - stored.timestamp < MAX_STALE_LIFETIME_MS) {
    memory = stored;
    return stored.defs;
  }

  return [];
}

export function invalidatePackageDefsCache() {
  memory = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
